import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { FeaturedPreviewItem } from '../../config/featuredTemplates';
import { HOME_TOPBAR_H } from '../home/layout/HomeTopBar';
import { useFeaturedPreviewStore } from '../../stores/featuredPreviewStore';
import { useAuthStore } from '../../stores/authStore';
import { ReportDialog } from './ReportDialog';

/* 中央大图与中段区域左右/底部的最小留白：图片 contain 缩放时不贴边（保底呼吸位约
   2.5 倍圆角半径，比例差空隙会叠加其上）；顶部不留白，图片可上探至区域顶缘 */
const STAGE_PADDING = 40;

/**
 * 图像作品预览（设计稿 188:19）——即梦式三段流式布局，无整页缩放
 * 渲染在 FeaturedPreviewPage 的流式内容区（顶栏下方、侧边栏右缘 → 视口右缘/下缘）
 * 内，根容器 h-full 随内容区弹性伸缩，元素尺寸恒定（字号不缩放）：
 * - 顶部用户栏与返回按钮在页面壳层（FeaturedPreviewPage 挂首页同款 HomeTopBar，
 *   原生流式几何，与首页视觉完全一致）
 * - 左侧作品列表列 80 宽贴左：宽 80 固定、高按图片原始比例自适应（竖图 142 /
 *   方图 107 / 横图 45），间距 12，未选 opacity 0.45、选中全亮度白描边 4 圆角 4
 *   （点击切换整个作品，数据 = 全部图像作品列表；右栏图集才是当前作品内的图片切换）
 * - 中央大图：按图片原始比例等比缩放（contain，小图也放大）撑满中段区域
 *   （左右/底部留 40px 留白不贴边、顶部不留白）并居中、
 *   圆角 16（不锁定竖版框，横版不裁切）；区域 = 左栏右缘(104)→右栏左缘(480)的
 *   弹性间隙（容器左右双锚定随窗口宽度伸缩，高度随内容区全高）
 *   （随缩略图/右栏图集联动切换）
 * - 作品组切换按钮（192:11）36x88 = 两个 36x36 圆角 12 #2D2D2D 白色箭头垂直堆叠
 *   间距 16，右缘距右栏左缘 24、垂直居中锚定（top-1/2）：上一/下一作品，
 *   与左栏列表同序、首尾环绕
 * - 右栏 480 宽贴右、全高（背景上探至视口顶，顶部不预留用户栏位）bg #181818：flex 纵向布局（顶部固定 + 图集固定 +
 *   提示词及以下弹性滚动），字号/图标/锚点按设计稿 251:159（作者@86：头像 20/用户名 14px/
 *   关注 48x24 文字 10px/点赞 20/数字 14px Medium；标题@140 12px Light、日期行
 *   12px；图集@208 格 80x142 间距 8；提示词@382 标签 14px、正文 14px 行高 24；
 *   参数@932 12px 底座 16x16；按钮 416x48 文字 16px Medium）——提示词、参数
 *   与按钮同在滚动区按文档流顺排：参数距提示词底 16、按钮距参数 32（251:159
 *   锚点节奏），位置随提示词长短动态变化、不贴底固定；内容超高时滚动区
 *   纵向滚动（图集固定不滚），提示词以下跟随滚动；
 *   右栏可折叠：左缘 36x36 圆角 12 手柄（top-8 避开垂直居中的作品组切换按钮），
 *   收起时右栏 translate-x-full 滑出、中央大图与作品组按钮平滑扩展补位，
 *   手柄留在屏幕右缘、箭头翻转（收起 → / 展开 ←），300ms 同步过渡
 */
export const FeaturedImagePreview: React.FC<{
  item: FeaturedPreviewItem;
  /** 全部图像作品列表（左栏 = 作品切换列表，与首页展示栏同源同序）；
      未传时回退为仅当前作品 */
  items?: FeaturedPreviewItem[];
  /** 点击「查看制作流程」：打开当前作品的只读画布项目（由 AppNew 传入） */
  onOpenWorkflow?: (item: FeaturedPreviewItem) => void;
}> = ({ item, items, onOpenWorkflow }) => {
  const openWork = useFeaturedPreviewStore((s) => s.open);
  const gallery = item.images && item.images.length ? item.images : [item.url];
  const [activeIdx, setActiveIdx] = useState(0);
  const [followed, setFollowed] = useState(false);
  /* 举报入口（方案 3.4）：仅用户发布的真实作品可举报（wf-/v- 前缀 = 模板/演示卡）；
     未登录先弹登录框，登录成功后经 pendingAction 回到举报弹层 */
  const [reportOpen, setReportOpen] = useState(false);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const openLoginModal = useAuthStore((s) => s.openLoginModal);
  const openReport = () => {
    if (!isLoggedIn) {
      openLoginModal(() => setReportOpen(true));
      return;
    }
    setReportOpen(true);
  };
  /* 右栏折叠：收起后右栏整体滑出右缘，中央大图区右缘扩展到屏幕边缘
     （舞台实测尺寸由 ResizeObserver 自动重算，大图随新区域重新 contain 适配） */
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  /* 中央大图自适应：原图分辨率可能很小（如模板缩略图），
     max-width/max-height 只缩不放会导致「显示很小」——
     记录图片原始宽高，按中段容器「实测尺寸」contain 等比缩放（小图放大、大图缩小）；
     容器尺寸随父级画布弹性变化（窗口拉伸时 contentW 变宽），用 ResizeObserver 监听随时重算 */
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [stageSize, setStageSize] = useState<{ w: number; h: number } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // 切换作品后右栏图集/中央大图从当前作品第一张开始
  useEffect(() => {
    setActiveIdx(0);
  }, [item.id]);

  // 换图时先清掉上一张的原始尺寸，避免新图按旧尺寸闪现（onLoad 后重新计算）
  useEffect(() => {
    setNatural(null);
  }, [activeIdx, item.id]);

  // 中段容器实测尺寸：初挂即取一次；父级画布随窗口拉伸（contentW 变化）时 ResizeObserver 重取
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setStageSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // contain 适配：等比缩放到撑满中段区域，左右/底部留 40px 呼吸空间（不贴边、
  // 不裁切），顶部不留白可上探至区域顶缘；垂直居中在扣除顶部后的可用带内完成
  const fitSize = useMemo(() => {
    if (!natural || !stageSize) return null;
    const availW = Math.max(stageSize.w - STAGE_PADDING * 2, 1);
    const availH = Math.max(stageSize.h - STAGE_PADDING, 1);
    const scale = Math.min(availW / natural.w, availH / natural.h);
    return { w: Math.round(natural.w * scale), h: Math.round(natural.h * scale) };
  }, [natural, stageSize]);

  /* 图集条横向滚动：可视宽 416 只能完整显示约 4.7 格（5 格总宽 432，第五张露出一部分
     暗示后面还有）；选中格变化（点缩略图或箭头）时把被裁的选中格平滑滚入可视区 */
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const left = activeIdx * (80 + 8); // 格宽 80 + 间距 8
    const right = left + 80;
    let target = strip.scrollLeft;
    if (left < strip.scrollLeft) target = left;
    else if (right > strip.scrollLeft + strip.clientWidth) target = right - strip.clientWidth;
    if (target !== strip.scrollLeft) strip.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeIdx]);

  const step = (delta: number) => {
    setActiveIdx((i) => (i + delta + gallery.length) % gallery.length);
  };

  /* 左栏作品列表：点击切换整个作品，选中张按 id 判断 */
  const workList = items && items.length ? items : [item];
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  /* 任一缩略图加载完成都会改变列表布局（高度按图片比例自适应），
     每次加载都触发一次重新居中校正，保证高亮图最终精准停在可视区中间 */
  const [imgTick, setImgTick] = useState(0);
  const mountedRef = useRef(false);

  /* 把选中缩略图滚动到列表可视区中间（设计稿 190:390：高亮图锚定中部，
     上方作品向上排、下方作品向下排）；用 rect 差值计算，不依赖 offsetParent */
  const centerSelected = useCallback((smooth: boolean) => {
    const list = listRef.current;
    const el = selectedRef.current;
    if (!list || !el) return;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const delta = elRect.top + elRect.height / 2 - (listRect.top + listRect.height / 2);
    if (Math.abs(delta) > 1) {
      list.scrollTo({ top: list.scrollTop + delta, behavior: smooth ? 'smooth' : 'auto' });
    }
  }, []);

  // 切换作品：新选中项平滑滚动到列表中间（首次挂载交给下方图片加载校正，立即到位）
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    centerSelected(true);
  }, [item.id, centerSelected]);

  // 图片陆续加载完成后布局高度才稳定：立即校正（任一张加载都会触发）
  useEffect(() => {
    centerSelected(false);
  }, [imgTick, centerSelected]);

  // 切换作品 = open 新作品 + 同步地址栏 query（replaceState 不触发 popstate 无循环），
  // 保证刷新/直链恢复的就是当前正在看的作品
  const switchWork = (next: FeaturedPreviewItem) => {
    openWork(next);
    const url = new URL(window.location.href);
    url.searchParams.set('type', next.type);
    url.searchParams.set('id', next.id);
    window.history.replaceState(null, '', url.toString());
  };

  // 当前作品在列表中的索引（右侧作品组切换按钮用）
  const workIdx = Math.max(
    workList.findIndex((w) => w.id === item.id),
    0,
  );
  // 上一/下一作品切换（与左栏列表同序，首尾环绕）；仅一件作品时点击无操作
  const stepWork = (delta: number) => {
    const next = workList[(workIdx + delta + workList.length) % workList.length];
    if (next && next.id !== item.id) switchWork(next);
  };

  return (
    <div className="relative h-full w-full bg-[#121212]">
      {/* 左侧作品列表列（宽 80 固定、高按图片原始比例，未选 opacity 0.45、
          选中全亮度白描边 4 圆角 4，点击切换整个作品）；高亮图始终锚定在
          全高可视区正中间，切换作品时列表滚动到新选中项居中，多作品可滚隐藏滚动条；
          maxHeight 100% 跟随内容区高度（矮窗口下列表滚动区同步收缩，不溢出裁切） */}
      <div className="absolute left-6 top-0 z-10 flex h-full w-20 flex-col justify-center">
        <div
          ref={listRef}
          className="flex w-20 min-h-0 flex-col gap-3 no-scrollbar"
          style={{ maxHeight: '100%', overflowY: 'auto' }}
        >
          {workList.map((it) => {
            const selected = it.id === item.id;
            return (
              <button
                key={it.id}
                ref={selected ? selectedRef : undefined}
                type="button"
                onClick={() => {
                  if (!selected) switchWork(it);
                }}
                className="relative w-20 flex-shrink-0 cursor-pointer overflow-hidden"
                style={{
                  borderRadius: 4,
                  opacity: selected ? 1 : 0.45,
                  boxShadow: selected ? '0 0 0 4px #FFFFFF' : 'none',
                }}
              >
                <img
                  src={it.coverUrl || (it.images && it.images[0]) || it.url}
                  alt={it.title}
                  className="block h-auto w-full"
                  onLoad={() => setImgTick((t) => t + 1)}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* 中央大图：双锚定容器（左 = 左栏右缘 104，右 = 右栏左缘 480/折叠后 0）——
          父级画布在宽屏下会被拉伸（contentW > 1680，多出宽度进入中段间隙），
          固定宽度容器会偏离中段中心，双锚定让容器始终精确覆盖中段区域；
          图片按容器实测尺寸 contain 等比缩放「放大或缩小」，左右/底部留
          STAGE_PADDING=40px 不贴边、顶部不留白，可用带内居中、加载前隐藏防闪现。
          图片 width/height 带 300ms 过渡：右栏折叠/展开时容器 right 与图片尺寸
          同步缓动（RO 每帧更新目标值、CSS 过渡平滑收敛），大图流式缩放补位。
          容器 pointer-events-none 不挡左栏交互，
          左栏/返回按钮/右栏 z-10 浮在图上层 */}
      <div
        ref={stageRef}
        className={`pointer-events-none absolute left-[104px] top-0 h-full transition-[right] duration-300 ease-out ${
          panelCollapsed ? 'right-0' : 'right-[480px]'
        }`}
      >
        <img
          key={activeIdx}
          src={gallery[activeIdx]}
          alt={item.title}
          onLoad={(e) => {
            const nw = e.currentTarget.naturalWidth;
            const nh = e.currentTarget.naturalHeight;
            if (!nw || !nh) return;
            setNatural({ w: nw, h: nh });
          }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl"
          style={{
            /* 顶部不留白：垂直居中基准 = 可用带 [0, h-40]，带中心较容器中心上移
               20px（margin 先于 translate 生效），满高图片可贴至区域顶缘 */
            marginTop: -STAGE_PADDING / 2,
            width: fitSize?.w,
            height: fitSize?.h,
            visibility: fitSize ? 'visible' : 'hidden',
            /* 尺寸变化平滑收敛：右栏折叠/展开的 300ms 期间 RO 每帧重算目标值，
               由本过渡插值缓动（首次挂载无旧值不会误过渡，切图先置 hidden 也不受影响） */
            transition: 'width 300ms ease-out, height 300ms ease-out',
          }}
        />
      </div>

      {/* 作品组切换按钮（整体 36x88 = 两个 36x36 圆角 12 #2D2D2D 按钮垂直堆叠
          间距 16，右缘距右栏左缘 24、垂直居中锚定 top-1/2 随内容区高度自适应）：
          上一/下一作品，与左栏列表同序、首尾环绕 */}
      <div
        className={`absolute top-1/2 z-10 flex -translate-y-1/2 flex-col gap-4 transition-all duration-300 ease-out ${
          panelCollapsed ? 'right-6' : 'right-[504px]'
        }`}
      >
        <button
          type="button"
          aria-label="上一作品"
          onClick={() => stepWork(-1)}
          className="block h-9 w-9 cursor-pointer transition-opacity hover:opacity-80"
        >
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="36" height="36" rx="12" fill="#2D2D2D" />
            <path
              d="M24 21L18 15L12 21"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          aria-label="下一作品"
          onClick={() => stepWork(1)}
          className="block h-9 w-9 cursor-pointer transition-opacity hover:opacity-80"
        >
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="36" height="36" rx="12" fill="#2D2D2D" />
            <path
              d="M24 15L18 21L12 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* 右栏折叠/展开手柄（36x36 圆角 12 #2D2D2D 白色 chevron，与作品组切换按钮
          同视觉语言）：独立于右栏定位、不随右栏滑出——展开时右缘贴右栏左缘、
          top-8 与作者行齐平（避开垂直居中的作品组切换按钮）；收起时平滑滑到
          屏幕右缘，箭头方向随状态翻转（收起 → / 展开 ←），300ms 与右栏同步 */}
      <button
        type="button"
        aria-label={panelCollapsed ? '展开信息栏' : '收起信息栏'}
        title={panelCollapsed ? '展开信息栏' : '收起信息栏'}
        onClick={() => setPanelCollapsed((c) => !c)}
        className={`absolute top-8 z-20 block h-9 w-9 cursor-pointer transition-all duration-300 ease-out hover:opacity-80 ${
          panelCollapsed ? 'right-2' : 'right-[480px]'
        }`}
      >
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="36" height="36" rx="12" fill="#2D2D2D" />
          {panelCollapsed ? (
            <path
              d="M21 13L15 18L21 23"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <path
              d="M15 13L21 18L15 23"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </button>

      {/* 右栏 480 宽贴右、全高（z-10 浮在满屏大图上层）：flex 纵向布局——
          顶部（作者/标题）与图集固定，提示词、参数与按钮同在滚动区按文档流
          顺排（251:159：参数距提示词底 16、按钮距参数 32），参数/按钮位置
          随提示词长短动态变化、不贴底固定；内容超出本区高度时滚动区收缩
          并纵向滚动（图集切换常驻可见，提示词以下内容跟随滚动）。
          顶部用户栏在页面壳层（FeaturedPreviewPage 挂首页同款 HomeTopBar），
          右栏背景上探至视口顶（顶部不预留顶栏位置，同设计稿「面板含用户栏区」；
          顶栏 z-[200] 悬浮其上），栏内内容仍自顶栏下方起算——
          paddingTop = 14 + HOME_TOPBAR_H，作者行位置与改造前完全一致。
          内容宽 416 = 480−64 与设计稿一致。
          折叠：translate-x-full 整体滑出右缘（页面根容器 overflow-hidden 裁切，
          无横向滚动条），300ms 与舞台/手柄同步 */}
      <div
        className={`absolute right-0 z-10 flex w-[480px] flex-col bg-[#181818] px-8 pb-[52px] transition-transform duration-300 ease-out ${
          panelCollapsed ? 'translate-x-full' : 'translate-x-0'
        }`}
        style={{ top: -HOME_TOPBAR_H, bottom: 0, paddingTop: 14 + HOME_TOPBAR_H }}
      >
        {/* 作者信息（设计稿 251:159 @(32,86) 416x24，减 72 → 面板内 @14）：
            头像 20 圆形、用户名 14px Regular、关注 48x24 r8 bg #222222（文字 10px +
            加号 12x12）、点赞图标 20 + 数字 14px Medium 右对齐 */}
        <div className="flex h-6 w-full shrink-0 items-center">
          <img
            src={item.avatar || '/images/home/avatars/avatar-user.png'}
            alt=""
            className="h-5 w-5 rounded-full object-cover"
          />
          <span className="ml-2 text-[14px] leading-[14px] text-white">{item.author}</span>
          <button
            type="button"
            onClick={() => setFollowed((f) => !f)}
            className="ml-3 flex h-6 w-12 cursor-pointer items-center justify-center gap-1 rounded-lg bg-[#222222] transition-colors hover:bg-[#2D2D2D]"
          >
            {!followed && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 2.5V9.5" stroke="white" strokeLinecap="round" />
                <path d="M2.5 6L9.5 6" stroke="white" strokeLinecap="round" />
              </svg>
            )}
            <span className="text-[10px] leading-[10px] text-white">{followed ? '已关注' : '关注'}</span>
          </button>
          {!item.id.startsWith('wf-') && !item.id.startsWith('v-') && (
            <button
              type="button"
              onClick={openReport}
              className="ml-2 cursor-pointer text-[10px] leading-[10px] text-[#8A8A8A] transition-colors hover:text-white"
            >
              举报
            </button>
          )}
          <div className="ml-auto flex items-center">
            <img src="/icons/home/icon-like.svg" alt="" className="h-5 w-5" />
            <span className="ml-2 text-[14px] font-medium leading-[14px] text-white">{item.likes}</span>
          </div>
        </div>

        {/* 标题 + 日期/声明（设计稿 @(32,140) 416x36，减 72 → @68：作者行底
            14+24=38 → mt-[30px]；标题 12px Light、日期行 12px，块高 36；
            层级靠颜色区分：标题白、日期/声明灰） */}
        <div className="mt-[30px] w-full shrink-0">
          <p className="text-[12px] font-light leading-[12px] tracking-[0.04em] text-white">{item.title}</p>
          <div className="mt-3 flex items-center text-[12px] leading-[12px] tracking-[0.04em] text-[#8A8A8A]">
            <span>{item.date}</span>
            <span className="mx-3 h-[8px] w-px bg-white/15" />
            <span>内容由AI生成</span>
          </div>
        </div>

        {/* 图集行（固定不随页面滚动、常驻可见；设计稿图集 @(32,208)，减 72 → @136：
            标题区底 68+36=104 → mt-8 = 32）：格子 80x142 间距 8（设计稿
            x=0/88/176/264/352），可视宽 416 只容得下「4 张 + 第五张的一部分」——
            第五张被裁切暗示后面还有，左右箭头滚动查看；选中白描边 3 圆角 4（inset
            画在格子内，避免被横向裁切容器裁掉），箭头 24x24 实底深色（#0D0D0D 90%
            圆角 8）@8,59 / @380,59 垂直居中 */}
        <div
          ref={stripRef}
          className="relative mt-8 flex h-[142px] w-full shrink-0 items-center gap-2 overflow-x-auto no-scrollbar"
        >
          {gallery.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              className="relative h-[142px] w-20 flex-shrink-0 cursor-pointer overflow-hidden rounded"
              style={{
                opacity: i === activeIdx ? 1 : 0.45,
                boxShadow: i === activeIdx ? 'inset 0 0 0 3px #FFFFFF' : 'none',
              }}
            >
              <img src={src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="上一张"
            className="absolute left-2 top-1/2 z-10 block h-6 w-6 -translate-y-1/2 cursor-pointer transition-opacity hover:opacity-80"
          >
            <img src="/icons/featured/gallery-arrow-left.svg" alt="" className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="下一张"
            className="absolute right-3 top-1/2 z-10 block h-6 w-6 -translate-y-1/2 cursor-pointer transition-opacity hover:opacity-80"
          >
            <img src="/icons/featured/gallery-arrow-right.svg" alt="" className="h-6 w-6" />
          </button>
        </div>

        {/* 提示词滚动区（设计稿提示词 @(32,382)，减 72 → @310：图集底 136+142=278
            → mt-8 = 32）：提示词、参数与按钮在本区内按文档流顺排（无弹性空隙），
            参数/按钮位置随提示词长短动态变化、不贴底固定；内容超出本区高度时
            纵向滚动（no-scrollbar 与左栏列表风格一致，滚轮/触控板可滚；
            图集固定在上方不随滚动，提示词以下内容跟随滚动） */}
        <div className="mt-8 flex min-h-0 w-full flex-1 flex-col overflow-y-auto no-scrollbar">
          {/* 图片提示词（标签 14px、正文 14px 行高 24 justified（设计稿正文
              @(0,30)，标签底 14 → mt-4 = 16）；shrink-0 保证不被压缩，
              过长时由本区滚动查看 */}
          <div className="w-full shrink-0">
            <p className="text-[14px] font-light leading-[14px] text-[#8A8A8A]">图片提示词</p>
            <p
              className="mt-4 text-[14px] leading-[24px] text-white"
              style={{ textAlign: 'justify' }}
            >
              {item.prompt || '暂无提示词'}
            </p>
          </div>

          {/* 参数信息（设计稿 @(32,932) 416x16）：紧跟提示词底 mt-4 = 16（251:159
              提示词底 916 → 参数顶 932）；智能参考(16x16 底座 + 文字) | 模型 | 比例 |
              画质，文字 12px #8A8A8A 0.04em、分隔线 1x8 */}
          <div className="mt-4 flex h-4 w-full shrink-0 items-center text-[12px] leading-[12px] tracking-[0.04em] text-[#8A8A8A]">
            {item.hasSmartRef && (
              <>
                <span className="flex h-4 w-4 items-center justify-center rounded bg-[#2D2D2D]">
                  <Sparkles size={10} className="text-white/80" />
                </span>
                <span className="ml-2">智能参考</span>
                <span className="mx-3 h-2 w-px bg-white/15" />
              </>
            )}
            <span>{item.model}</span>
            <span className="mx-3 h-2 w-px bg-white/15" />
            <span>{item.aspect}</span>
            <span className="mx-3 h-2 w-px bg-white/15" />
            <span>{item.quality}</span>
          </div>

          {/* 查看制作流程（设计稿 @(32,980) 416x48 r12）：参数底 948 +32 → mt-8；
              文字 16px Medium，随文档流顺排（不贴底固定）；
              点击打开当前作品对应的只读画布项目（WorkflowPreviewPage）；
              仅发布时公开了源项目制作流程快照的作品显示（demo/模板卡/旧记录无 workflowData 不展示入口） */}
          {item.workflowData && (
            <button
              type="button"
              onClick={() => onOpenWorkflow?.(item)}
              className="mt-8 h-12 w-full shrink-0 cursor-pointer rounded-xl bg-[#2D2D2D] text-[16px] font-medium leading-[48px] text-white transition-colors hover:bg-[#3A3A3A]"
            >
              查看制作流程
            </button>
          )}
        </div>
      </div>

      {/* 举报弹层（portal 挂 body，不受右栏/顶栏层叠影响） */}
      <ReportDialog open={reportOpen} workId={item.id} onClose={() => setReportOpen(false)} />
    </div>
  );
};
